import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { getRenewalPlanPeriod, notifyEmployees, submitEmployeeWithRenewalClasses } from 'core'
import { GchraRenewalTaskIds, TaskStatus } from 'core/application/adminDashboard/adminTasks'
//
import { getActivePlanPeriod } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import {
  RenewalGroupHealthPlanInformationFormDTO,
  getGroupHealthPlanDetails,
  updateGroupHealthPlanInformation,
} from 'core/application/adminDashboard/benefitRenewal/renewalGroupHealthPlanInformation/renewalGroupHealthPlanInformation'
import { updateRenewalPaymentInformation } from 'core/application/adminDashboard/benefitRenewal/renewalPaymentInformation/renewalPaymentInformation'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'
import { Product } from 'core/domain/product/Product'

import useSharedState from 'common/hooks/useSharedState'

import { Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useBreakPoints, useLoginUser, useQueryRequestV2 } from 'common/hooks'
import { useQueryRequest, useRenewalTasks } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import Icon from 'assets/images/perks-dashboard-home-setup-icon.svg'

import ConfirmEmployeeClassesModal from '../../common/ConfirmEmployeeClasses/ConfirmEmployeeClassesModal'
import SetupProgress from '../../common/adminTaskStep/SetupProgress'
import SetupProgressSteps from '../../common/adminTaskStep/SetupProgressSteps'
import InviteEmployeesModal from '../../common/inviteYourEmployeesModal'
import ConfirmPaymentInformationModal from '../../groupHra/ConfirmPaymentDetailsInformationModal/ConfirmPaymentDetailsInformation'
import ConfirmGroupHealthPlanInformationModal from '../PlanRenewal/ConfirmGroupHealthPlanInformationModal'

const GchraRenewalTasks = () => {
  const history = useHistory()
  const { t: translate } = useTranslation()

  const [tasks, isLoading, reloadTasks] = useRenewalTasks()
  const [user] = useLoginUser()
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
  const [activePlanPeriod] = useQueryRequest(null, () => getActivePlanPeriod())
  const [renewalPlanPeriod] = useQueryRequest(null, () => getRenewalPlanPeriod())
  const [confirmPaymentModal, setConfirmPaymentModal] = useState<boolean>()
  const [confirmEmployeeClassesModal, setConfirmEmployeeClassesInformationModal] = useState<boolean>(false)
  const [confirmGroupHealthPlanInformationModal, setConfirmGroupHealthInformationModal] = useState<boolean>(false)
  const [inviteEmployeesModalVisible, setInviteEmployeesModalVisible] = useState<boolean>()
  const { isLg } = useBreakPoints()

  const { data: groupHealthPlanDetails } = useQueryRequestV2<RenewalGroupHealthPlanInformationFormDTO>({
    initialState: null,
    queryFn: () => getGroupHealthPlanDetails(),
  })

  const [, setPageTitle] = useSharedState<{
    title: string
    showBackButton: boolean
  }>('PageTitle')

  const taskCallbackMapper = new Map<number, () => void>()
  taskCallbackMapper.set(GchraRenewalTaskIds.ConfirmPaymentDetails, () => setConfirmPaymentModal(true))
  taskCallbackMapper.set(GchraRenewalTaskIds.ConfirmGroupHealthPlanDetails, () => {
    setConfirmGroupHealthInformationModal(true)
  })

  taskCallbackMapper.set(GchraRenewalTaskIds.ConfirmEmployeeClasses, () =>
    setConfirmEmployeeClassesInformationModal(true),
  )
  taskCallbackMapper.set(GchraRenewalTaskIds.FinalizeBenefitRenewal, () =>
    history.push(`${ROUTE_PATHS.LEGAL_DOCUMENTS_SIGN_DOCUMENTS}/${renewalPlanPeriod.id}?returnTo=RenewalTasks`),
  )

  taskCallbackMapper.set(GchraRenewalTaskIds.RedesignBenefit, () => {
    const url =
      renewalPlanPeriod.status === BenefitDesignStatus.Completed ||
      renewalPlanPeriod.status === BenefitDesignStatus.Signed
        ? `${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`
        : `${ROUTE_PATHS.BENEFIT_DETAILS_PATH}`
    history.push(url)
  })
  taskCallbackMapper.set(GchraRenewalTaskIds.NotifyEmployees, () => setInviteEmployeesModalVisible(true))

  useEffect(() => {
    setPageTitle({
      title: `Benefit renewal for ${new Date().getFullYear()}`,
      showBackButton: true,
    })

    return () => {
      setPageTitle(null)
    }
  }, [])

  useEffect(() => {
    if (tasks) {
      setActiveStepIndex(tasks.findIndex((x) => x.status == TaskStatus.Active))
    }
  }, [tasks])

  return (
    <Skeleton loading={isLoading}>
      {tasks && (
        <>
          <div className="container-100-percent flex fd-col mt-32 inner-padding bg-white" style={{ maxWidth: 700 }}>
            <div className="main-wrapper inner-padding flex flex__column">
              {tasks.length > 0 && (
                <>
                  <div>
                    <div className="align_self_center mt-24 mb-24 text-center">
                      <img src={Icon} alt="dashboard-logo" className={isLg ? '' : 'w-100'} />
                    </div>

                    <h1>{user.firstName}, let’s renew your benefit plan </h1>

                    {activePlanPeriod && (
                      <p className="gray-7 mb-16">
                        You need to confirm or change your existing elections by{' '}
                        {parseZoneFormat(activePlanPeriod.endDate)}, when your benefit will launch and we notify your
                        employees of the new benefit.
                      </p>
                    )}
                  </div>
                  <SetupProgress title={'Benefit Renewal'} steps={tasks} activeStep={activeStepIndex} />
                  <SetupProgressSteps
                    steps={tasks}
                    activeStep={activeStepIndex}
                    onStepClick={(taskId) => {
                      taskCallbackMapper.get(taskId)()
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {confirmPaymentModal && (
            <ConfirmPaymentInformationModal
              updateRenewalPaymentInformationHandler={(payload) => updateRenewalPaymentInformation(payload)}
              onRenewalPaymentInfoUpdated={async () => {
                await reloadTasks()
                setConfirmPaymentModal(false)
              }}
              onCancel={() => setConfirmPaymentModal(false)}
            />
          )}

          {confirmGroupHealthPlanInformationModal && (
            <ConfirmGroupHealthPlanInformationModal
              updateRenewalGroupHealthPlanDetailsSubmit={(payload) => updateGroupHealthPlanInformation(payload)}
              onRenewalGroupPlanInformationUpdated={async () => {
                await reloadTasks()
                setConfirmGroupHealthInformationModal(false)
              }}
              groupHealthPlanDetails={groupHealthPlanDetails}
              onCancel={() => setConfirmGroupHealthInformationModal(false)}
            />
          )}

          {confirmEmployeeClassesModal && (
            <ConfirmEmployeeClassesModal
              productId={Product.GroupHraId}
              confirmEmployeeClassesHandler={(payload) => submitEmployeeWithRenewalClasses(payload)}
              onConfirmEmployeeClasses={async () => {
                setConfirmEmployeeClassesInformationModal(false)
                await reloadTasks()
              }}
              onCancel={() => {
                setConfirmEmployeeClassesInformationModal(false)
              }}
            />
          )}

          {renewalPlanPeriod && (
            <>
              <InviteEmployeesModal
                visible={inviteEmployeesModalVisible}
                benefitStartDate={renewalPlanPeriod.startDate}
                onCancel={() => setInviteEmployeesModalVisible(false)}
                title={'Notify your employees'}
                infoMessage={'Peoplekeep will send your employees an email informing them of their new benefit.'}
                inviteApi={notifyEmployees}
                onSendInviteToEmployees={() => {
                  window.location.href = ROUTE_PATHS.ADMIN_DASHBOARD_PATH
                }}
                showAllEmployeesAddedCheckBox={false}
                checkboxLabel={translate('admin-dashboard.home.invite-alert.perks-checkbox-label')}
              />
            </>
          )}
        </>
      )}
    </Skeleton>
  )
}

export default GchraRenewalTasks
